import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { IssueSmartRequestSummaryEntryPointContext } from '@atlassian/jira-issue-view-smart-request-summary-entrypoint/src/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import { totalCommentsSelector } from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { PROACTIVE_COMMENT_SUMMARY_ENABLED } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useHasScrolledLayoutContainer } from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';

const runtimeProps = {};

export const IssueSmartRequestSummaryEntryPointContainerOld = () => {
	const { entryPointReferenceSubject } = useContext(IssueSmartRequestSummaryEntryPointContext);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="issueSmartRequestSummary"
			packageName="issue"
			teamName="Bento"
			runtimeProps={runtimeProps}
			fallback={null}
			errorFallback="flag"
		/>
	);
};

export const IssueSmartRequestSummaryEntryPointContainerAutoLoad = () => {
	const { entryPointReferenceSubject, entryPointActions } = useContext(
		IssueSmartRequestSummaryEntryPointContext,
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		entryPointActions.load();
	}, [entryPointActions]);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="issueSmartRequestSummary"
			packageName="issue"
			teamName="Bento"
			runtimeProps={runtimeProps}
			fallback={null}
			errorFallback="flag"
		/>
	);
};

const PROACTIVE_COMMENT_THRESHOLD = 5;

export const IssueSmartRequestSummaryEntryPointContainerWithProactiveLoad = () => {
	const { entryPointReferenceSubject, entryPointActions } = useContext(
		IssueSmartRequestSummaryEntryPointContext,
	);

	const totalComments = useSelector(totalCommentsSelector);
	const locale = useLocale();
	const [hasProactiveFeatureEnabled] = useUserPreferencesValue(PROACTIVE_COMMENT_SUMMARY_ENABLED);
	const isEnglishLocale = locale && locale.startsWith('en');
	const [hasScrolled] = useHasScrolledLayoutContainer();
	const smartRequestSummaryHasBeenLoaded = useRef(false);

	const isProactiveExperimentConditionMet = useMemo(
		() =>
			totalComments >= PROACTIVE_COMMENT_THRESHOLD &&
			isEnglishLocale &&
			hasProactiveFeatureEnabled === 'true',
		// We only need to check the initial values, as the entry point will be loaded only once
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (
			smartRequestSummaryHasBeenLoaded.current === false &&
			hasScrolled &&
			isProactiveExperimentConditionMet
		) {
			entryPointActions.load();

			smartRequestSummaryHasBeenLoaded.current = true;
		}
	}, [
		hasScrolled,
		smartRequestSummaryHasBeenLoaded,
		isProactiveExperimentConditionMet,
		entryPointActions,
	]);

	const localRuntimeProps = useMemo(
		() => ({ isProactiveExperimentConditionMet }),
		[isProactiveExperimentConditionMet],
	);

	return (
		<JiraEntryPointContainer
			entryPointReferenceSubject={entryPointReferenceSubject}
			id="issueSmartRequestSummary"
			packageName="issue"
			teamName="Bento"
			runtimeProps={localRuntimeProps}
			fallback={null}
			errorFallback="flag"
		/>
	);
};

export const IssueSmartRequestSummaryEntryPointContainer = componentWithFG(
	'improve_issue_view_comment_ai',
	IssueSmartRequestSummaryEntryPointContainerWithProactiveLoad,
	IssueSmartRequestSummaryEntryPointContainerAutoLoad,
);
